import React, {
    forwardRef,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';

/**
 * The one place on a page that says what an action just did.
 *
 * Mounted whether or not it holds anything, for two reasons: a live region
 * inserted into the page at the same moment as its text is not reliably
 * announced, and the page focuses this element after an action, which needs a
 * node that was already there.
 *
 * The messages are Bootstrap alert boxes rather than react-bootstrap's
 * `<Alert>`, whose own `role="alert"` inside a polite region would announce
 * the same sentence twice, once assertively and once as the region changes.
 */
const ActionNotice = forwardRef(function ActionNotice(
    { notice = '', error = '' },
    ref
) {
    return (
        <div role="status" tabIndex={-1} ref={ref}>
            {error && <div className="alert alert-danger">{error}</div>}
            {notice && <div className="alert alert-success">{notice}</div>}
        </div>
    );
});

ActionNotice.propTypes = {
    notice: PropTypes.string,
    error: PropTypes.string,
};

/**
 * The state behind one page's notice region, and the focus move with it.
 *
 * The button a member activates is often unmounted by its own success: a
 * request panel drops a row the moment the request leaves `open`, and React
 * then drops focus to the body, so the next Tab restarts at the skip link at
 * the top of the page. Moving focus to the sentence that says what happened
 * both reads it and leaves the reader somewhere sensible.
 *
 * The move happens in an effect rather than in the handler, so the message is
 * in the DOM before focus lands on the element holding it.
 *
 * `focus: false` reports without moving anything, for an action whose own
 * control is still on the page and still has something to say to it: taking
 * focus away from a message box a member is about to retype is worse than
 * leaving the region to announce itself.
 */
export function useActionNotice() {
    const ref = useRef(null);
    const [state, setState] = useState({ notice: '', error: '', moves: 0 });

    useEffect(() => {
        if (state.moves > 0) {
            ref.current?.focus();
        }
    }, [state.moves]);

    const report = useCallback(
        ({ notice = '', error = '', focus = true }) =>
            setState((previous) => ({
                notice,
                error,
                moves: focus ? previous.moves + 1 : previous.moves,
            })),
        []
    );

    return { ref, notice: state.notice, error: state.error, report };
}

export default ActionNotice;
